import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { VFC } from 'react';

import classNames from 'classnames';
import { ChevronRight as ChevronRightIcon } from '@vgno/icons';

import { Chip } from '../Chip/Chip';
import { useControlled } from '../utils/useControlled';
import { usePlaceholders } from '../PageComponents/usePlaceholders';

import * as styles from './ChipStack.module.scss';

export interface ChipItem {
  id: string;
  label: string;
  type?: string;
  items?: ChipItem[];
}

export interface ChipStackProps {
  chips: ChipItem[];
  isLoading?: boolean;
  selection?: string[];
  onSelectionChange?: (ids: string[]) => void;
}

interface Item {
  id: string;
  parentId?: string;
  label: string;
  isActive: boolean;
  displaySeparator: boolean;
}

const createItems = (chip: ChipItem, ids: string[], items: Item[]): Item[] => {
  if (!ids.length) {
    items.push(
      ...(chip.items ?? []).map((item, index) => {
        return {
          id: item.id,
          parentId: chip.id,
          label: item.label,
          isActive: false,
          displaySeparator: index === 0,
        };
      })
    );

    return items;
  }

  const nextChip = chip.items?.find((item) => item.id === ids[0])!;

  items.push({
    id: nextChip.id,
    parentId: chip.id,
    label: nextChip.label,
    isActive: true,
    displaySeparator: true,
  });

  return createItems(nextChip, ids.slice(1), items);
};

const ChipStack: VFC<ChipStackProps> = ({
  chips,
  isLoading,
  selection,
  onSelectionChange,
}) => {
  const [showStartShadow, setShowStartShadow] = useState(false);
  const [showEndShadow, setShowEndShadow] = useState(false);
  const startThresholdRef = useRef<HTMLDivElement>(null!);
  const endThresholdRef = useRef<HTMLDivElement>(null!);

  const [selectedChipIds = [], setSelectedChipIds] = useControlled({
    controlled: selection,
    default: [],
    name: 'ChipStack',
    state: 'selection',
  });

  const placeholders = usePlaceholders(!isLoading, 4);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];

      if (entry.target === startThresholdRef.current) {
        setShowStartShadow(!entry.isIntersecting);
      } else {
        setShowEndShadow(!entry.isIntersecting);
      }
    });

    observer.observe(startThresholdRef.current);
    observer.observe(endThresholdRef.current);

    return () => observer.disconnect();
  }, []);

  const items = useMemo(() => {
    if (!selectedChipIds.length) {
      return chips.map((chip) => {
        return {
          id: chip.id,
          parentId: undefined,
          label: chip.label,
          isActive: false,
          displaySeparator: false,
        };
      });
    }

    const currentChip = chips.find((chip) => chip.id === selectedChipIds[0])!;

    return createItems(currentChip, selectedChipIds.slice(1), [
      {
        id: currentChip.id,
        parentId: undefined,
        label: currentChip.label,
        isActive: true,
        displaySeparator: false,
      },
    ]);
  }, [chips, selectedChipIds]);

  const handleChange = (id: string, parentId?: string) => {
    const index = selectedChipIds.findIndex((chipId) => chipId === id);

    let newSelectedChipIds;

    if (index === -1) {
      if (parentId === selectedChipIds[selectedChipIds.length - 1]) {
        newSelectedChipIds = [...selectedChipIds, id];
      } else {
        newSelectedChipIds = [...selectedChipIds.slice(0, -1), id];
      }
    } else {
      newSelectedChipIds = selectedChipIds.slice(0, index);
    }

    setSelectedChipIds(newSelectedChipIds);

    if (onSelectionChange) {
      onSelectionChange(newSelectedChipIds);
    }
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.startShadow]: showStartShadow,
        [styles.endShadow]: showEndShadow,
      })}
    >
      <div
        className={styles.scrollContainer}
        aria-busy={isLoading}
        aria-multiselectable
        role="tablist"
      >
        <div ref={startThresholdRef} />
        {placeholders.map((_, index) => (
          <Chip key={index} aria-hidden loading label="..." />
        ))}
        {!isLoading &&
          items.map((item, index) => (
            // usage of index based key is intended here - this way we can keep chip's transition
            <Fragment key={index}>
              {item.displaySeparator && (
                <div className={styles.separator}>
                  <ChevronRightIcon />
                </div>
              )}
              <Chip
                label={item.label}
                isActive={item.isActive}
                onClick={() => handleChange(item.id, item.parentId)}
              />
            </Fragment>
          ))}
        <div ref={endThresholdRef} />
      </div>
    </div>
  );
};

export { ChipStack };
